/*
手順の板の印。正本はサーバーの記録 (api-go の cloudeditor_step_progress) で、書くのは sandbox の gateway だけ。
受講生が端末で yumesaku check と打っても、板の「確かめる」を押しても、gateway がその人のコンテナで
同じ判定 (yumesaku check --json) を動かし、通ったものだけを記録する。板は記録を読んで印を付ける。

条件の無い手順 (本人が「できた」を押すもの) は、api-go の印 (marks.ts) に記録し、ブラウザにも数を控える (guides.ts の readProgress)。
*/
import { sessionFetch } from "./session-fetch";
import { MAX_HINT_LEVEL, answerHintLevel, machineChecksOf } from "./answer-policy";
import type { Guide, GuideCheck, GuideStep, HintRung } from "./guides";

export interface StepProgress {
  passed: string[];
  hints: Record<string, number>;
  /** 章が指すレッスンを、本人がもう完了しているか (サーバの記録)。api-go が付けなかった (読めなかった、旧い api-go) ときは無い */
  lessonDone?: boolean;
  /** 章の合格で済みにする学習パスの項目が、いま済みか (サーバの記録)。そういう項目の章でないとき、読めなかったときは無い */
  pathItemDone?: boolean;
}

/**
 * 落ちた確かめの、あなたの答えと正しい答えの並び。採点の道具 (lesson-check/check.py) が書き、
 * yumesaku (judge.go の CheckDiff) と gateway (cli.go の cliDiff) を通って届く。hidden の試験と道場では expected が無い
 */
export interface CheckDiff {
  kind: "value" | "output" | "error";
  /** greet("太郎") など。出力の比べでは無し */
  call?: string;
  actual: string;
  expected?: string;
  hidden?: true;
}

/** 落ちたときの 2 行と、並べた違い。通ったときは passed だけ */
export interface CheckVerdict {
  step: string;
  passed: boolean;
  fail?: string;
  next?: string;
  ran?: string;
  output?: string;
  /** 落ちた条件の番号 (0 から)。judge.go の Failed */
  failed?: number;
  /** 英語の誤りを日本語にした 1 行 */
  meaning?: string;
  diff?: CheckDiff;
  /** 落ちた種類。時間切れのときだけ "timeout" (judge.go が印を付けない間は、fail の文言で見分ける) */
  kind?: "timeout";
}

/** 時間切れで落ちたときに、梯子の代わりに出す固定の 1 文 */
export const TIMEOUT_NOTE = "無限ループや入力待ちが無いか見直してください。";

/** 時間切れの枠の見出し。処理名と秒数は fail の文言から取り、読めなければ「確かめ」と 15 秒 */
export function timeoutHeading(verdict: Pick<CheckVerdict, "fail"> | null | undefined): string {
  const m = /(?:^|\s)(\S+(?: \S+)*?) が (\d+) 秒たっても終わりませんでした/.exec(verdict?.fail ?? "");
  const seconds = m?.[2] ?? /(\d+) 秒たっても終わりませんでした/.exec(verdict?.fail ?? "")?.[1] ?? "15";
  return `${m ? `${m[1]} を` : "確かめを"} ${seconds} 秒で打ち切りました`;
}

/** 確かめが時間切れで落ちたか。答えの誤りではないので、条件のヒントの梯子は開かない */
export function isTimeoutVerdict(verdict: Pick<CheckVerdict, "passed" | "kind" | "fail"> | null | undefined): boolean {
  if (!verdict || verdict.passed) return false;
  return verdict.kind === "timeout" || /\d+ 秒たっても終わりませんでした/.test(verdict.fail ?? "");
}

const DIFF_KINDS = ["value", "output", "error"];

/** 形の合わない diff は捨てる。hidden なら、expected があっても持たない */
export function parseDiff(value: unknown): CheckDiff | undefined {
  if (!value || typeof value !== "object") return undefined;
  const d = value as Record<string, unknown>;
  if (typeof d.kind !== "string" || !DIFF_KINDS.includes(d.kind) || typeof d.actual !== "string") return undefined;
  if (d.call !== undefined && typeof d.call !== "string") return undefined;
  if (d.expected !== undefined && typeof d.expected !== "string") return undefined;
  const diff: CheckDiff = { kind: d.kind as CheckDiff["kind"], actual: d.actual };
  if (d.call) diff.call = d.call as string;
  if (d.hidden === true) diff.hidden = true;
  else if (d.expected !== undefined) diff.expected = d.expected as string;
  return diff;
}

/** 道場 (確認テストの代わり) の章か。答えも期待値も受講生に出さない。索引の dojo と同じく、id の頭が dojo- */
export function isDojoGuide(guideId: string): boolean {
  return guideId.startsWith("dojo-");
}

/** チューターへ渡す diff。道場の章と hidden の試験では、正しい答え (expected) を落とし、hidden だけ残す */
export function diffForTutor(diff: CheckDiff, guideId: string): CheckDiff {
  if (!diff.hidden && !isDojoGuide(guideId)) return diff;
  const out: CheckDiff = { ...diff, hidden: true };
  delete out.expected;
  return out;
}

export function parseProgress(value: unknown): StepProgress | null {
  if (!value || typeof value !== "object") return null;
  const { passed, hints, lessonDone, pathItemDone } = value as { passed?: unknown; hints?: unknown; lessonDone?: unknown; pathItemDone?: unknown };
  if (!Array.isArray(passed)) return null;
  const out: StepProgress = { passed: passed.filter((id): id is string => typeof id === "string"), hints: {} };
  if (hints && typeof hints === "object") {
    for (const [k, v] of Object.entries(hints)) if (typeof v === "number") out.hints[k] = v;
  }
  if (typeof lessonDone === "boolean") out.lessonDone = lessonDone;
  if (typeof pathItemDone === "boolean") out.pathItemDone = pathItemDone;
  return out;
}

/** 章の合格で済みにする学習パスの項目の、板が持ついまの完了。done が null は読めなかった回 (前の「済み」を出し続けない) */
export interface PathItemDoneState {
  guide: string;
  done: boolean | null;
  /** 最後に読めた値。読めない回をはさんでも「済みにしました」を二度知らせないため */
  last: boolean | null;
  /** 開いてから「まだ」を読んだか。読んだあとに済みになったときだけ知らせる */
  seenUndone: boolean;
}

/** 1 回の読み取り (失敗、401、欄が無いときは progress が null か "unauthorized" か欄なし) から、次の状態と、済みになったと知らせるかを決める */
export function nextPathItemDone(current: PathItemDoneState | null, guide: string, progress: StepProgress | "unauthorized" | null): { state: PathItemDoneState; announce: boolean } {
  const done = progress && progress !== "unauthorized" && typeof progress.pathItemDone === "boolean" ? progress.pathItemDone : null;
  const same = current?.guide === guide;
  const announce = done === true && same && current.last === false && current.seenUndone;
  return { state: { guide, done, last: done ?? (same ? current.last : null), seenUndone: (same && current.seenUndone) || done === false }, announce };
}

/**
 * 自分の記録を読む。Worker の /api/* は api-go へそのまま中継される (ログインの Cookie で本人)。
 * 401 は "unauthorized" で返す。ログインが切れた画面が 8 秒ごとに 401 を取り続けないよう、呼び手が読み取りを止める
 */
export async function fetchStepProgress(guideId: string, fetchImpl: typeof fetch = sessionFetch): Promise<StepProgress | null | "unauthorized"> {
  try {
    const response = await fetchImpl(`/api/cloudeditor/progress?guide=${encodeURIComponent(guideId)}`, { credentials: "same-origin" });
    if (response.status === 401) return "unauthorized";
    if (!response.ok) return null;
    return parseProgress(await response.json());
  } catch {
    return null;
  }
}

export type RestartResult = { kind: "restarted"; progress: StepProgress } | { kind: "failed"; message: string };

/**
 * 「最初から」。api-go が本人のこの章の合格の記録を外し、外したあとの進みを返す。
 * 板だけで印を隠すと、端末の yumesaku check (gateway はサーバの記録で採点する) が「ぜんぶ終わりました」と言い、
 * 板は 0 のままで進めなくなる (2026-10-20 本番)。外せなかったときは、板も戻さない
 */
export async function postRestart(guideId: string, fetchImpl: typeof fetch = sessionFetch): Promise<RestartResult> {
  const failed = { kind: "failed", message: "いまは最初に戻せませんでした。少し待ってから、もう一度押してください" } as const;
  let response: Response;
  try {
    response = await fetchImpl("/api/cloudeditor/progress/restart", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guide: guideId }),
    });
  } catch {
    return { kind: "failed", message: "最初に戻せませんでした。通信を確かめて、もう一度押してください" };
  }
  if (response.status === 401) return { kind: "failed", message: "ログインが切れています。ページを読み直してから、もう一度押してください" };
  if (!response.ok) return failed;
  const progress = parseProgress(await response.json().catch(() => null));
  return progress ? { kind: "restarted", progress } : failed;
}

/** 章の「試せた」で済まない学習パス v2 の項目の案内。済みの口は項目の頁の「済みにする」だけ */
/** 済ませたあとにこの章でもう一度押すと、api-go は項目の済みを読んで通し、板が最後まで進む (板は項目の済みを自分では読まない) */
export const ITEM_CONDITIONS_UNMET_TEXT = "この項目は、学習パスの項目の頁でミニテストに答えて「済みにする」を押すと済みになります。済ませたら、ここに戻ってもう一度押します";

export type ReadDoneResult = { kind: "completed" } | { kind: "failed"; message: string };

/**
 * 本文だけの章 (Guide.readOnly) で「読み終えた」を押したとき。api-go が、ブラウザの text レッスンと同じ完了
 * (MarkLessonComplete。証拠は要らない) に通す。Worker の /api/* は api-go へそのまま中継され、本人はログインの Cookie で決まる
 * (送り元の確かめも api-go の外側が見る)。環境 (Pod) は使わない
 */
export async function postReadDone(guideId: string, fetchImpl: typeof fetch = sessionFetch): Promise<ReadDoneResult> {
  let response: Response;
  try {
    response = await fetchImpl("/api/cloudeditor/read-done", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guide: guideId }),
    });
  } catch {
    return { kind: "failed", message: "完了を記録できませんでした。通信を確かめて、もう一度押してください" };
  }
  if (response.status === 401) return { kind: "failed", message: "ログインが切れています。ページを読み直してから、もう一度押してください" };
  if (response.status === 404) return { kind: "failed", message: "この章は、読み終えた印では完了になりません" };
  // 学習パス v2 の項目で、ミニテストが残っている (api-go の CLOUDEDITOR_ITEM_CONDITIONS_UNMET)。待っても通らないので、済ませる場所を言う
  if (response.status === 409) return { kind: "failed", message: ITEM_CONDITIONS_UNMET_TEXT };
  if (!response.ok) return { kind: "failed", message: "いまは完了を記録できません。少し待ってから、もう一度押してください" };
  const json = (await response.json().catch(() => null)) as { completed?: unknown } | null;
  return json?.completed === true ? { kind: "completed" } : { kind: "failed", message: "完了を記録できませんでした。もう一度押してください" };
}

export type CheckResult =
  | { kind: "verdict"; verdict: CheckVerdict; progress: StepProgress | null; recorded: boolean }
  | { kind: "unsupported" }
  | { kind: "failed"; message: string };

/** gateway に「いまの手順を確かめて」と頼む。VS Code のホスト (Cookie で本人) の /__sandbox/check */
/** 門番は確かめを 40 秒で打ち切って答える。それより長く返らないのは途中の道で詰まったときなので、待つのをやめて言う */
export const CHECK_FETCH_MS = 60_000;

export async function requestCheck(origin: string, guideId: string, fetchImpl: typeof fetch = fetch, timeoutMs: number = CHECK_FETCH_MS): Promise<CheckResult> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/__sandbox/check?guide=${encodeURIComponent(guideId)}`, { method: "POST", credentials: "include", signal: AbortSignal.timeout(timeoutMs) });
  } catch (error) {
    if (error instanceof DOMException && error.name === "TimeoutError") return { kind: "failed", message: "時間内に確かめの答えが返りませんでした。少し待ってから、もう一度押してください" };
    return { kind: "failed", message: "確かめられませんでした。通信を確かめて、もう一度押してください" };
  }
  // まだ確かめる口の無い gateway。板は「できた」で進める今までの形に戻る
  if (response.status === 404) return { kind: "unsupported" };
  if (response.status === 429) return { kind: "failed", message: "続けて押されました。少し待ってから押してください" };
  if (!response.ok) return { kind: "failed", message: "いまは確かめられません。少し待ってから、もう一度押してください" };
  const json = (await response.json().catch(() => null)) as { verdict?: Record<string, unknown>; progress?: unknown; recorded?: unknown } | null;
  const v = json?.verdict;
  if (!v || typeof v.passed !== "boolean") return { kind: "failed", message: "確かめた結果を読めませんでした" };
  const text = (x: unknown) => (typeof x === "string" && x ? x : undefined);
  return {
    kind: "verdict",
    verdict: {
      step: text(v.step) ?? "", passed: v.passed, fail: text(v.fail), next: text(v.next), ran: text(v.ran), output: text(v.output),
      failed: typeof v.failed === "number" && v.failed >= 0 ? v.failed : undefined,
      meaning: text(v.meaning),
      diff: parseDiff(v.diff),
      kind: v.kind === "timeout" ? "timeout" : undefined,
    },
    progress: parseProgress(json?.progress),
    recorded: json?.recorded === true,
  };
}

/** サーバの採点 (ブラウザのレッスンの提出と同じ SubmitCode) の結果。api-go の提出の答えのうち、板に出すもの */
export interface SubmitResult {
  isCorrect: boolean;
  message: string;
  failedAttempts: number;
  limitExceeded: boolean;
  remainingSubmissions: number;
  /** 元のレッスンが完了になっているか */
  completed: boolean;
}

export type SubmitOutcome =
  | { kind: "result"; result: SubmitResult }
  /** この章はサーバで採点しない、またはまだ提出の口の無い gateway */
  | { kind: "unsupported"; message: string }
  /** retryAfterSeconds は 429 (続けて出した) のときだけ。待つ秒数 (応答の Retry-After か文から読み、読めなければ 2) */
  | { kind: "failed"; message: string; retryAfterSeconds?: number };

const UNSUPPORTED_SUBMIT = "この章は、いま手順パネルから提出できません。ターミナルで yumesaku submit と打つか、「確かめる」で進めます";

/** 待つ秒数が読めないときの秒数 */
const SUBMIT_RETRY_DEFAULT_SECONDS = 2;
/** これより長く待つと言われたら、待たずに押し直しを案内する */
const SUBMIT_RETRY_MAX_SECONDS = 30;

/** 429 の応答が示す「待つ秒数」。Retry-After の秒数、無ければ gateway の文の「n 秒」、どちらも読めなければ 2 秒 */
function retryAfterSeconds(response: Response, message: string): number {
  const header = Number(response.headers.get("Retry-After"));
  if (Number.isFinite(header) && header > 0) return Math.ceil(header);
  const said = /(\d+)\s*秒/.exec(message);
  const seconds = said ? Number(said[1]) : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? seconds : SUBMIT_RETRY_DEFAULT_SECONDS;
}

/**
 * gateway に「この章のファイルをサーバで採点して」と頼む。VS Code のホスト (Cookie で本人) の /__sandbox/submit。
 * 出すファイルと本人は gateway が決める (ここからはコードも「通った」も送らない)。
 */
export async function requestSubmit(origin: string, guideId: string, fetchImpl: typeof fetch = fetch): Promise<SubmitOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/__sandbox/submit?guide=${encodeURIComponent(guideId)}`, { method: "POST", credentials: "include" });
  } catch {
    return { kind: "failed", message: "提出できませんでした。通信を確かめて、もう一度押してください" };
  }
  const json = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  // gateway の断り ({message}) と api-go の断り ({error: {message}}) のどちらも、日本語の文をそのまま使う
  const said = (): string | undefined => {
    const direct = json?.message;
    if (typeof direct === "string" && direct) return direct;
    const nested = (json?.error as { message?: unknown } | undefined)?.message;
    return typeof nested === "string" && nested ? nested : undefined;
  };
  if (response.status === 404) return { kind: "unsupported", message: said() ?? UNSUPPORTED_SUBMIT };
  if (response.status === 429) {
    const message = said() ?? "続けて押されました。少し待ってから押してください";
    return { kind: "failed", message, retryAfterSeconds: retryAfterSeconds(response, message) };
  }
  if (!response.ok) return { kind: "failed", message: said() ?? "いまは提出できません。少し待ってから、もう一度押してください" };
  if (!json || typeof json.isCorrect !== "boolean") return { kind: "failed", message: "採点の結果を読めませんでした" };
  const count = (x: unknown) => (typeof x === "number" && Number.isFinite(x) ? x : 0);
  return {
    kind: "result",
    result: {
      isCorrect: json.isCorrect,
      message: typeof json.message === "string" ? json.message : "",
      failedAttempts: count(json.failedAttempts),
      limitExceeded: json.limitExceeded === true,
      remainingSubmissions: count(json.remainingSubmissions),
      completed: json.completed === true,
    },
  };
}

/** 板で書いて出す宿題の提出。ファイルの場所と本人は gateway が決めるので、ここからは文と URL だけを送る */
export interface HandInInput {
  note: string;
  url: string;
}

/** 提出の前の「添えるファイル」の一覧。gateway が提出と同じ読み方で読んだもの (中身は持たない) */
export type HandInFiles =
  | {
    kind: "listed";
    /** 添えるファイル (場所と大きさ)。表のファイルが先で、章のフォルダの中のものが続く */
    files: { path: string; size: number }[];
    /** 文字として読めないので添えないファイル (名前は上限まで。excludedCount は全部の数) */
    excluded: string[];
    excludedCount: number;
    limits: { files: number; fileBytes: number; totalBytes: number };
  }
  | { kind: "failed"; message: string };

/**
 * gateway に「いま提出したら添えるファイル」の一覧を聞く。VS Code のホスト (Cookie で本人) の /__sandbox/handin-files。
 * 上限を超えるときは、提出と同じ案内 (どのファイルが大きいか、上限) が message に入る
 */
export async function requestHandInFiles(origin: string, guideId: string, fetchImpl: typeof fetch = fetch): Promise<HandInFiles> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/__sandbox/handin-files?guide=${encodeURIComponent(guideId)}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "text/plain" },
      body: "{}",
    });
  } catch {
    return { kind: "failed", message: "添えるファイルを読めませんでした。通信を確かめて、読み直してください" };
  }
  const json = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  const count = (x: unknown) => (typeof x === "number" && Number.isFinite(x) && x >= 0 ? x : 0);
  const message = typeof json?.message === "string" && json.message ? json.message : undefined;
  if (!response.ok || json?.ok !== true) return { kind: "failed", message: message ?? "添えるファイルを読めませんでした。少し待ってから、読み直してください" };
  const files = Array.isArray(json.files) ? json.files.filter((f): f is { path: string; size: number } =>
    typeof f?.path === "string" && typeof f?.size === "number") : [];
  const excluded = Array.isArray(json.excluded) ? json.excluded.filter((p): p is string => typeof p === "string") : [];
  const limits = json.limits as { files?: unknown; fileBytes?: unknown; totalBytes?: unknown } | undefined;
  return {
    kind: "listed",
    files,
    excluded,
    excludedCount: Math.max(count(json.excludedCount), excluded.length),
    limits: { files: count(limits?.files), fileBytes: count(limits?.fileBytes), totalBytes: count(limits?.totalBytes) },
  };
}

export type HandInOutcome =
  /** 保存された。recorded は板の印 (提出の手順の合格) まで付いたか */
  | { kind: "submitted"; message: string; recorded: boolean }
  /** この章は、板から提出を受けない (口の無い gateway、api-go の表に無い章) */
  | { kind: "unsupported"; message: string }
  | { kind: "failed"; message: string };

/**
 * gateway に「この章のファイルと、この文と URL を、宿題の提出として出して」と頼む。VS Code のホスト (Cookie で本人) の /__sandbox/handin。
 * 本文は text/plain の JSON (事前の確認の要らない形。ほかの板の要求と同じ)。
 * 添えるファイルの場所、本人、行き先の課題は gateway と api-go が決め、ここからは送らない
 */
export async function requestHandIn(origin: string, guideId: string, input: HandInInput, fetchImpl: typeof fetch = fetch): Promise<HandInOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/__sandbox/handin?guide=${encodeURIComponent(guideId)}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ note: input.note, url: input.url }),
    });
  } catch {
    return { kind: "failed", message: "提出できませんでした。通信を確かめて、もう一度押してください" };
  }
  const json = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  // gateway の断り ({message}) と api-go の断り ({error: {message}}) のどちらも、日本語の文をそのまま使う
  const said = (): string | undefined => {
    const direct = json?.message;
    if (typeof direct === "string" && direct) return direct;
    const nested = (json?.error as { message?: unknown } | undefined)?.message;
    return typeof nested === "string" && nested ? nested : undefined;
  };
  if (response.status === 404) return { kind: "unsupported", message: said() ?? "この章は、いま手順パネルから提出できません" };
  if (!response.ok) return { kind: "failed", message: said() ?? "いまは提出できません。少し待ってから、もう一度押してください" };
  if (!json || json.submitted !== true) return { kind: "failed", message: "提出の結果を読めませんでした" };
  return {
    kind: "submitted",
    message: typeof json.message === "string" && json.message ? json.message : "提出しました",
    recorded: json.recorded === true,
  };
}

/**
 * 自動の提出。429 (続けて出している) なら、応答が示す秒数だけ待って、1 回だけ出し直す。
 * 出し直しも断られたら、そのときの断りを返す (呼び出し側が、板に理由を出して「提出する」を押すよう案内する)。
 * sleep のあと stillCurrent が false (章を替えた) なら、出し直さずに最初の断りを返す
 */
export async function requestSubmitWithRetry(
  origin: string,
  guideId: string,
  { fetchImpl = fetch, sleep = defaultSleep, stillCurrent = () => true }: { fetchImpl?: typeof fetch; sleep?: (ms: number) => Promise<void>; stillCurrent?: () => boolean } = {},
): Promise<SubmitOutcome> {
  const first = await requestSubmit(origin, guideId, fetchImpl);
  const wait = first.kind === "failed" ? first.retryAfterSeconds : undefined;
  if (wait === undefined || wait > SUBMIT_RETRY_MAX_SECONDS) return first;
  // 秒は切り上げて言われるが、時計の誤差の分だけ余分に待つ
  await sleep(wait * 1000 + 300);
  if (!stillCurrent()) return first;
  return requestSubmit(origin, guideId, fetchImpl);
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * 「確かめる」が通ったら、そのまま提出まで進めるか。サーバで採点する章 (guide.submit) で、最後の手順がいま通ったときだけ。
 * 初学者には、確かめるが通ったのに終わらないように見えるため。すでに完了している章と、通ったあとにもう一度押しただけのとき
 * (gateway は手順の名前を空で返す) は出し直さない。出し直すと、1 日の提出の回数を使ってしまう
 */
export function shouldAutoSubmit(guide: Guide, verdict: CheckVerdict, lessonDone: boolean): boolean {
  if (!guide.submit || lessonDone || !verdict.passed) return false;
  const last = guide.steps[guide.steps.length - 1];
  return !!last && verdict.step === last.id;
}

/**
 * 手順のヒントの段。1 段目 (hint) と 2 段目から (hints) を並べる。板の「ヒント」も、チューターへ渡す段の数も、これを数える
 */
export function hintTiers(step: Pick<GuideStep, "hint" | "hints"> | null | undefined): string[] {
  if (!step) return [];
  return [step.hint, ...(step.hints ?? [])].filter((h): h is string => Boolean(h));
}

/**
 * 学習パスの練習の確かめ (lesson-to-guide の item_procedures_shell.py) の next の頭。確かめの道具が、足りないことを出力の 1 行に書く
 */
const OUTPUT_LINE_NEXT = "出力の 1 行に";

/**
 * 落ちた確かめの出力のうち、足りないことを書いた 1 行。next が「出力の 1 行に」を指す確かめで、並べた違い (diff) の無いときだけ。
 * ほかの確かめの出力は、受講生に見せる文として書いていないので出さない
 */
export function checkOutputReason(verdict: Pick<CheckVerdict, "passed" | "next" | "output" | "diff"> | null | undefined): string | null {
  if (!verdict || verdict.passed || verdict.diff || !verdict.output || !verdict.next?.startsWith(OUTPUT_LINE_NEXT)) return null;
  const line = verdict.output.split("\n").map((l) => l.trim()).filter(Boolean).at(-1);
  return line ? line.slice(0, 300) : null;
}

/** 採点の道具が直す所を「NG 」の行で出す確かめ (ecsite の check.py) の next の目印。 next が「NG の行」を指すときだけ、その行を板に出す */
const NG_LINE_NEXT = "NG の行";

/**
 * 落ちた確かめの出力のうち、直す所を 1 行ずつ書いた「- 」の行 (next が「NG の行」を指すときは「NG 」の行、NG を外して)。next が「出ている - の行」を指す確かめで、並べた違い (diff) の無いときだけ。
 * 宿題の章の判定 (check.py) は、直す所を「- 」の行で出す。端末にしか出さないと、板の判定の欄には「まだ条件を満たしていません」だけが出て、
 * 何を直すか分からない。出力はあと 12 行までしか届かないので、届いた分を最大 6 行まで出す
 */
export function checkOutputBullets(verdict: Pick<CheckVerdict, "passed" | "next" | "output" | "diff"> | null | undefined): string[] {
  if (!verdict || verdict.passed || verdict.diff || !verdict.output) return [];
  if (verdict.next?.includes(NG_LINE_NEXT)) {
    return verdict.output.split("\n")
      .map((l) => l.trim())
      .filter((l) => l.startsWith("NG ") && l.length > 3)
      .map((l) => l.slice(3).trim().slice(0, 300))
      .slice(0, 6);
  }
  if (!verdict.next?.includes("- の行")) return [];
  return verdict.output.split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("- ") && l.length > 2)
    .map((l) => l.slice(2).trim().slice(0, 300))
    .slice(0, 6);
}

/** 落ちた条件に next が無いときの軽く押す一言。受講生の端末の道具 (CLI) が補う文と同じ */
const NUDGE_FALLBACK = "手順の説明をもう一度読んで、書き換えたファイルを保存しましょう";

/** 梯子の最後の段「答え」の釦の文字。答えの中身は板に持たず、押したあとに api-go から引く */
export const ANSWER_RUNG_TEXT = "答えを見る";

/**
 * 手順の段階ヒントの梯子 (軽く押す、強く押す、答えの順)。
 * 落ちた確かめがあるとき (verdict がその手順の落ちた判定)、
 * 軽く押す = 落ちた条件の next と、diff の呼び方 (「greet("太郎") の答えを見直します」)、
 * 強く押す = 落ちた条件の hints (無ければ手順の hint と hints)、
 * 答え = 答えを出す章 (answer-policy.ts) の solve だけ。中身は持たず、押したあとに api-go が返す。
 * 落ちていないときは、今までの hintTiers と同じ並び (答えの段は付けない)
 */
export function hintLadder(guide: Pick<Guide, "id" | "lessonId" | "steps">, step: GuideStep | null | undefined, verdict: CheckVerdict | null = null): HintRung[] {
  if (!step) return [];
  if (isTimeoutVerdict(verdict) && verdict?.step === step.id) return [];
  const plain = hintTiers(step);
  if (!verdict || verdict.passed || verdict.step !== step.id) {
    return plain.map((text, i): HintRung => ({ level: i === 0 ? "nudge" : "push", text }));
  }
  const check = verdict.failed === undefined ? undefined : machineChecksOf(step)[verdict.failed];
  const next = verdict.next ?? check?.next ?? NUDGE_FALLBACK;
  // 出力の違い (SQL の行など) の見出し (「4 行目から違います」) は関数の呼び出しではないので、「の答えを見直します」を付けない
  const call = verdict.diff?.kind === "output" ? undefined : verdict.diff?.call;
  let nudge = call ? `${next}\n${call} の答えを見直します` : next;
  let push = check?.hints?.length ? check.hints : plain;
  // 落ちた枠は判定の next を「次にやること」としてもう出している。1 段目に同じ文を繰り返さず、手順のヒントを 1 段目にする。
  // 答えの段のある手順は段の数が答えの条件になるので並びを変えない
  if (verdict.next && !call && push.length > 0 && answerHintLevel(guide, step) === null) {
    nudge = push[0];
    push = push.slice(1);
  }
  const ladder: HintRung[] = [{ level: "nudge", text: nudge }, ...push.map((text): HintRung => ({ level: "push", text }))];
  if (answerHintLevel(guide, step) !== null) ladder.push({ level: "answer", text: ANSWER_RUNG_TEXT });
  return ladder;
}

export interface AnswerFile {
  name: string;
  text: string;
}

export type AnswerOutcome =
  | { kind: "answer"; files: AnswerFile[] }
  /** 条件が揃っていない (全段を開いていない、2 回落ちていない)、またはこの手順は答えを出さない */
  | { kind: "locked"; message: string }
  | { kind: "failed"; message: string };

/**
 * 模範解答を api-go から引く (GET /api/cloudeditor/answer)。条件を判るのは api-go で、板は結果をそのまま出す。
 * 返ったら api-go が「見た」を記録する。Worker の /api/* は api-go へそのまま中継される (ログインの Cookie で本人)
 */
export async function fetchAnswer(guideId: string, stepId: string, fetchImpl: typeof fetch = sessionFetch): Promise<AnswerOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`/api/cloudeditor/answer?guide=${encodeURIComponent(guideId)}&step=${encodeURIComponent(stepId)}`, { credentials: "same-origin" });
  } catch {
    return { kind: "failed", message: "答えを開けませんでした。通信を確かめて、もう一度押してください" };
  }
  if (response.status === 401) return { kind: "failed", message: "ログインが切れています。ページを読み直してから、もう一度押してください" };
  const json = (await response.json().catch(() => null)) as { files?: unknown; error?: { code?: unknown; message?: unknown } } | null;
  if (response.status === 403) {
    const code = json?.error?.code;
    if (code === "CLOUDEDITOR_ANSWER_LOCKED") return { kind: "locked", message: "答えは、ヒントをすべて開いて、確かめに 2 回落ちたあとに開きます。もう一度確かめてみましょう" };
    if (code === "CLOUDEDITOR_PLAN_REQUIRED" && typeof json?.error?.message === "string") return { kind: "locked", message: json.error.message };
    return { kind: "locked", message: "この手順では、答えは出していません" };
  }
  if (response.status === 404) return { kind: "locked", message: "この手順の答えが見つかりません" };
  if (!response.ok) return { kind: "failed", message: "いまは答えを開けません。少し待ってから、もう一度押してください" };
  const files = Array.isArray(json?.files)
    ? json.files.flatMap((f): AnswerFile[] => {
      const file = f as { name?: unknown; text?: unknown };
      return typeof file.name === "string" && typeof file.text === "string" ? [{ name: file.name, text: file.text }] : [];
    })
    : [];
  return files.length > 0 ? { kind: "answer", files } : { kind: "failed", message: "答えを読めませんでした" };
}

/**
 * ヒントを n 段まで開いたことを api-go に書く (POST /api/cloudeditor/hint)。答えの条件の 1 つ (全段を開いた記録) になる。
 * 答えを出す手順 (answerHintLevel が数を返す手順) だけに送る。api-go はその手順で 1 回は落ちたあとの記録だけを受けるので、
 * 落ちる前に開いた段は、落ちたあとと、答えを引く直前に送り直す。書けたかを返す (送れなくても板の表示は変えない)
 */
export async function postHintLevel(guideId: string, stepId: string, level: number, fetchImpl: typeof fetch = sessionFetch): Promise<boolean> {
  try {
    const response = await fetchImpl("/api/cloudeditor/hint", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guide: guideId, step: stepId, level: Math.max(1, Math.min(MAX_HINT_LEVEL, Math.floor(level))) }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * 梯子の先頭から shown 段開いたとき、手順のヒント (hintTiers) のうち何段を見せたか。チューターへ渡す「見せた段」はこれ。
 * 落ちたあとの梯子は、先頭に「次にやること」を足し、条件ごとの hints に替わることがあるので、開いた段の数をそのまま渡さない
 */
export function tiersShown(step: Pick<GuideStep, "hint" | "hints"> | null | undefined, ladder: readonly HintRung[], shown: number): number {
  const tiers = hintTiers(step);
  const texts = ladder.filter((rung) => rung.level !== "answer").map((rung) => rung.text);
  const same = (rest: string[]) => rest.length === tiers.length && rest.every((text, i) => text === tiers[i]);
  const open = Math.max(0, shown);
  // 落ちていない梯子は手順のヒントそのもの
  if (same(texts)) return Math.min(open, tiers.length);
  // 落ちた梯子は先頭に「次にやること」を足す。続きが手順のヒントなら、先頭を除いて数える
  if (same(texts.slice(1))) return Math.min(Math.max(0, open - 1), tiers.length);
  // 条件ごとのヒントに替わった梯子は、手順のヒントを見せていない
  return 0;
}

/** 学習パスの今の項目 (番号は位置の帯と同じ 1 から数えた位置)。学習パスから来ていなければ null */
export interface PathItemLabel {
  /** 項目ページの頭と同じ番号 (地図の「1-17」) */
  index: number | string;
  title: string;
  /** 項目が複数のレッスンを指すとき、いまの章が何本目か。提出で完了になるのは、項目ではなくこのレッスン */
  lesson?: { index: number; total: number; /** 項目の中で、エディターの外 (学習パスの画面) で答える確認問題の本数 */ outside?: number } | null;
  /** いまの章が項目の前提や復習の章のとき、その役の名前 (事前学習、復習など)。合格しても項目の課題が済んだとは言わない */
  role?: string | null;
}

/** 「学習パスの 51 番「題」」。項目が分からないときは「このレッスン」。前提や復習の章は「学習パスの 51 番「題」の事前学習の章」 */
export function pathItemName(item: PathItemLabel | null | undefined, dojo = false): string {
  // 道場の問題は学習パスの完了に数えないので、項目の名前でなく問題と言う
  if (dojo) return "この問題";
  // 学習パスの位置が分からない (読めなかった、学習パスから来ていない) ときは、提出で完了になるレッスンとだけ言う。
  // 前提や復習の章かどうかも分からないので、学習パスの項目が済んだとは言わない
  if (!item) return "このレッスン";
  const name = `学習パスの ${item.index} 番「${item.title}」`;
  if (item.lesson && item.lesson.total > 1) return `${name}の ${item.lesson.index} 本目のレッスン${item.role ? ` (${item.role})` : ""}`;
  return item.role ? `${name}の${item.role}の章` : name;
}

/**
 * 板に出す提出の結果の文 (合格、まだ合格ではない理由、上限)。
 * 完了の言葉は 3 つに分ける。手順は「できた」、章は「合格」、学習パスの項目は「完了」
 */
export function describeSubmit(result: SubmitResult, pathItem: PathItemLabel | null = null, dojo = false): { tone: "ok" | "ng" | "limit"; title: string; detail: string } {
  if (result.isCorrect) {
    return {
      tone: "ok",
      title: "合格です",
      detail: result.completed ? `${pathItemName(pathItem, dojo)}が完了になりました` : result.message || "採点は通りましたが、完了を記録できませんでした。もう一度押してください",
    };
  }
  if (result.limitExceeded) {
    return {
      tone: "limit",
      title: "今日の提出の上限に達しました",
      detail: result.completed ? `前の合格で、${pathItemName(pathItem, dojo)}は完了しています` : result.message || "明日、また出せます",
    };
  }
  const detail = [result.message, result.remainingSubmissions > 0 ? `今日は、あと ${result.remainingSubmissions} 回出せます` : ""].filter(Boolean).join(" ");
  return { tone: "ng", title: "まだ合格ではありません", detail: detail || "ファイルを見直して、もう一度出しましょう" };
}

/** 本人の「できた」では進められない手順 (機械で確かめる手順と、自己申告の手順) */
const gated = (step: GuideStep): boolean => hasCheck(step) || attestOf(step) !== null;

/**
 * 板の「できた」の数。ブラウザに覚えた数から始め、サーバーの記録で通った手順の続きを進める。
 *
 * 条件の無い手順 (本人が「できた」を押すもの) はサーバーに記録が無く、押した数はブラウザにしか残らない。
 * 別の端末や、保存場所を消したブラウザで開くと 0 に戻り、章を全部通しても 1 / 3 に見える。
 * assumeSkipped が true のとき (このブラウザに数を覚えていないとき) は、条件の無い手順を、そのあとの最初の確かめられる手順が
 * サーバーで通っていれば済んだものとして進める (先の関門を通った人は、その前の手順を過ぎている)。
 * 覚えているとき (「最初から」で 0 に戻した場合を含む) は、覚えた数を尊重して、ここでは進めない。
 * いずれも、そのあとに確かめられる手順が無い、または通っていない条件の無い手順では止まる (本人が「できた」を押す)
 */
export function mergeDone(guide: Guide, localDone: number, passed: readonly string[], { assumeSkipped = false }: { assumeSkipped?: boolean } = {}): number {
  const set = new Set(passed);
  const { steps } = guide;
  let done = Math.max(0, Math.min(localDone, steps.length));
  while (done < steps.length) {
    if (!set.has(steps[done].id)) {
      if (!assumeSkipped || gated(steps[done])) break;
      const gate = steps.findIndex((step, index) => index > done && gated(step));
      if (gate === -1 || !set.has(steps[gate].id)) break;
    }
    done += 1;
  }
  return done;
}

export type StepState = "done" | "current" | "todo";

/**
 * 手順ごとの状態。手順の板の印 (緑のチェック、いまの手順) と、「n / m できた」の数は、この 1 つの結果から出す。
 *
 * 「できた」を押した数 (done) と、サーバーが機械で確かめて通した手順 (passed) は、別の材料で、手順の並びの先頭から続くとは限らない。
 * 条件の無い手順 (読む手順) を飛ばして、あとの手順の確かめが通ることがある。印だけを両方から出し、数を done だけから出すと、
 * 「0 / 3 できた」なのに 2 つ目の手順に緑のチェックが付くずれになる。
 * いまの手順 (done の番号) は、通っていても「できた」を押すまで current のまま。done が手順の数に届いたら、全部 done
 */
export function stepStates(steps: readonly { id: string }[], done: number, passed: readonly string[]): StepState[] {
  const total = steps.length;
  const current = Math.min(done, total - 1);
  const finished = done >= total;
  return steps.map((step, index) =>
    index < done || (index !== current && passed.includes(step.id)) ? "done" : index === current && !finished ? "current" : "todo");
}

/**
 * 板の最後の手順で押すと read-done (読み終えた印の完了) に通す章か。本文だけの章 (readOnly) と、
 * 項目の本文を読んで端末で試す章 (envFrom)。試す手順は確かめが無いので、完了は読むだけの章と同じ道で記録する
 */
export function completesByReading(guide: Pick<Guide, "readOnly" | "envFrom"> & { steps?: Guide["steps"] }): boolean {
  if (guide.steps && isQuizGuide({ steps: guide.steps })) return false;
  return guide.readOnly === true || guide.envFrom !== undefined;
}

/** 問いの手順 (GuideStep.quiz) を持つ章か。この章は「読み終えた」を出さず、問いの合格 (api-go の採点) で済む */
export function isQuizGuide(guide: Pick<Guide, "steps">): boolean {
  return guide.steps.some((step) => step.quiz !== undefined);
}

/**
 * 自己申告 (attest) と読むだけの手順しか無い章か。Pod の中でやることが無い (action も file も機械の確かめも無い) ので環境を起こさない。
 * 章の attestOnly の印は、この判定と一致させる (cloudeditor.test.ts が照らす)
 */
export function isAttestOnly(guide: Pick<Guide, "steps">): boolean {
  return guide.steps.some((step) => attestOf(step) !== null)
    && guide.steps.every((step) => step.action === undefined && step.file === undefined && !hasCheck(step));
}

/** この手順は機械で確かめられるか (板に「確かめる」を出すか) */
export function hasCheck(step: Guide["steps"][number]): boolean {
  const list = step.check === undefined ? [] : Array.isArray(step.check) ? step.check : [step.check];
  // 提出 (handed-in) は CLI の確かめではなく、板の「提出する」で付く印なので、「確かめる」は出さない
  return list.some((c) => c.type !== "manual" && c.type !== "handed-in" && c.type !== "attest");
}

/**
 * 手順の「できた」の条件の 1 行。実行の釦 (action が run) のある確かめの手順は、実行するだけでは印が付かず、「確かめる」を押して初めて通る。
 * 元の done は「端末に結果が出たら、できました」と言い切るので、押す釦を言い直す。確かめる釦が出ない板 (canCheck が false) では元のまま。
 * done が確かめと同じ端末の命令 (sh check.sh 01-login、go test など、check の label) を名指す手順も同じく言い直す。
 * 端末でその命令を打って OK が出ても板の記録は付かず、done のとおりに進むと手順が済みにならないため (2026-10-27、基本パス 2-10)
 */
export function doneLine(step: Pick<Guide["steps"][number], "done" | "action" | "check">, canCheck: boolean): string {
  // 写した章の決まり文句 (1600 章ほど) は言葉が詰まっているので、板では言い直す。端末の yumesaku guide は元の文のまま
  const done = step.done === "確かめるで、全部の試験が通ったら、できました" ? "「確かめる」で全部の試験が通ったら、できたになります" : step.done;
  if (!canCheck || !hasCheck(step as Guide["steps"][number])) return done;
  if (step.action !== "run" && !namesTerminalCheck(step, done)) return done;
  return done.replace(/、できました$/, "、「確かめる」を押します");
}

/** done が、確かめの label のうち端末で打てる命令 (英数と記号だけの label) を名指すか */
function namesTerminalCheck(step: Pick<Guide["steps"][number], "check">, done: string): boolean {
  return checkLabels(step as GuideStep).some((label) => /^[\x20-\x7e]+$/.test(label) && done.includes(label));
}

/** 手順の自己申告の定義 (check の attest)。無ければ null。申告は本人の言葉なので、機械の確かめ (hasCheck) とは別の道で進む */
export function attestOf(step: Guide["steps"][number]): Extract<GuideCheck, { type: "attest" }> | null {
  const list = step.check === undefined ? [] : Array.isArray(step.check) ? step.check : [step.check];
  return list.find((c): c is Extract<GuideCheck, { type: "attest" }> => c.type === "attest") ?? null;
}

/** 端末のレッスンの章で、板の「送る命令」の一覧の 1 行 (打った命令。skipped は、送らないと決めた行) */
export interface RecordLine {
  n: number;
  text: string;
  skipped: boolean;
}

export type RecordOp = { op: "list" } | { op: "skip"; skip: number[] } | { op: "reset" };

export type RecordOutcome =
  | { kind: "lines"; lines: RecordLine[] }
  /** この章に記録が無い、または口の無い gateway */
  | { kind: "unsupported" }
  | { kind: "failed"; message: string };

/**
 * gateway に、この章の「打った命令」の一覧を読み書きしてもらう。VS Code のホスト (Cookie で本人) の /__sandbox/record。
 * 提出するのは、外していない行と記録の頭から gateway の向こうで作る terminal.sh で、ここからは命令の中身を送らない (行の番号だけ)。
 * 本文は text/plain の JSON (事前の確認の要らない形。ほかの板の要求と同じ)
 */
export async function requestRecord(origin: string, guideId: string, op: RecordOp, fetchImpl: typeof fetch = fetch): Promise<RecordOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/__sandbox/record?guide=${encodeURIComponent(guideId)}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(op),
    });
  } catch {
    return { kind: "failed", message: "記録を読めませんでした。通信を確かめてください" };
  }
  const json = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (response.status === 404) return { kind: "unsupported" };
  if (!response.ok) {
    const said = typeof json?.message === "string" && json.message ? json.message : "いまは記録を読めません。少し待ってください";
    return { kind: "failed", message: said };
  }
  if (!json || typeof json.ok !== "boolean" || !Array.isArray(json.lines)) return { kind: "failed", message: "記録を読めませんでした" };
  if (!json.ok) return { kind: "unsupported" };
  const lines: RecordLine[] = [];
  for (const l of json.lines as unknown[]) {
    const row = l as { n?: unknown; text?: unknown; skipped?: unknown };
    if (typeof row.n === "number" && Number.isInteger(row.n) && typeof row.text === "string") lines.push({ n: row.n, text: row.text, skipped: row.skipped === true });
  }
  return { kind: "lines", lines };
}

/** 写した章の確かめの落ちた文 (lesson-to-guide の item_procedures_*.py) の、結果の側の言い方 */
const GENERATED_FAIL_RESULT = /(がエラーで止まりました|出てきた行が違います|にその書き方が見つかりません|にその書き方があります|流したあとの表の中身が違います|出力にその行が見つかりません)$/;

/**
 * 確かめの落ちた文を、条件の名前と結果に分ける。写した章の文は「条件。結果」の 1 文で、
 * 「エラーなく動く。python3 CheckGreeting.py がエラーで止まりました」のように、条件が通ったように読めてしまう。
 * 条件が手順の確かめの名前 (label) か、結果が写した章の決まった言い方のときだけ分ける。手で書いた文は分けない (null)
 */
export function splitFail(fail: string, labels: readonly string[] = []): { condition: string; result: string } | null {
  const at = fail.indexOf("。");
  if (at <= 0 || at === fail.length - 1) return null;
  const condition = fail.slice(0, at);
  const result = fail.slice(at + 1);
  if (result.includes("。")) return null;
  return labels.includes(condition) || GENERATED_FAIL_RESULT.test(result) ? { condition, result } : null;
}

/** 手順の確かめに付いた名前 (command-output と command-succeeds の label) */
export function checkLabels(step: GuideStep): string[] {
  const list = step.check === undefined ? [] : Array.isArray(step.check) ? step.check : [step.check];
  return list.flatMap((check) => "label" in check && typeof check.label === "string" ? [check.label] : []);
}
